"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdventureScriptsParser = exports.AdventureScriptValidationError = void 0;
const adventureScriptValidation_1 = require("../engine/adventureScriptValidation");
class AdventureScriptValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AdventureScriptValidationError';
    }
}
exports.AdventureScriptValidationError = AdventureScriptValidationError;
/**
 * Extracts, validates, and strips adventure-level `## Scripts` sections.
 */
class AdventureScriptsParser {
    /**
     * Extract script blocks from merged adventure text, validate prelude shape, strip section for parsing.
     */
    static processAdventureText(adventureText) {
        const errors = [];
        const sectionContent = this.extractScriptsSectionContent(adventureText);
        const textWithoutScriptsSection = this.stripScriptsSection(adventureText);
        if (!sectionContent || !sectionContent.trim()) {
            return { scripts: undefined, textWithoutScriptsSection, errors };
        }
        let blocks;
        try {
            blocks = this.extractTripleQuotedScriptBlocks(sectionContent);
        }
        catch (e) {
            errors.push(e instanceof Error ? e.message : String(e));
            return { scripts: undefined, textWithoutScriptsSection, errors };
        }
        if (blocks.length === 0) {
            return { scripts: undefined, textWithoutScriptsSection, errors };
        }
        const scripts = blocks.map((b) => b.trim()).filter(Boolean).join('\n\n');
        if (!scripts) {
            return { scripts: undefined, textWithoutScriptsSection, errors };
        }
        try {
            (0, adventureScriptValidation_1.validatePrelude)(scripts);
        }
        catch (e) {
            errors.push(e instanceof Error ? e.message : String(e));
        }
        return {
            scripts: errors.length > 0 ? undefined : scripts,
            textWithoutScriptsSection,
            errors
        };
    }
    /** Extract raw body of the first `## Scripts` section (may contain multiple `'''` blocks). */
    static extractScriptsSectionContent(text) {
        const lines = text.split('\n');
        const content = [];
        let inScripts = false;
        let found = false;
        for (const line of lines) {
            const trimmed = line.trim();
            if (this.isScriptsSectionHeader(trimmed)) {
                inScripts = true;
                found = true;
                continue;
            }
            if (inScripts) {
                if (trimmed.startsWith('## ') && !this.isScriptsSectionHeader(trimmed)) {
                    break;
                }
                content.push(line);
            }
        }
        return found ? content.join('\n') : null;
    }
    /** Remove `## Scripts` section from adventure text. */
    static stripScriptsSection(text) {
        const lines = text.split('\n');
        const out = [];
        let inScripts = false;
        for (const line of lines) {
            const trimmed = line.trim();
            if (this.isScriptsSectionHeader(trimmed)) {
                inScripts = true;
                continue;
            }
            if (inScripts) {
                if (trimmed.startsWith('## ') && !this.isScriptsSectionHeader(trimmed)) {
                    inScripts = false;
                    out.push(line);
                }
                continue;
            }
            out.push(line);
        }
        return out.join('\n');
    }
    /** Extract all `'''...'''` script blocks from section body. */
    static extractTripleQuotedScriptBlocks(sectionContent) {
        const blocks = [];
        let i = 0;
        const text = sectionContent;
        while (i < text.length) {
            const start = text.indexOf("'''", i);
            if (start === -1) {
                break;
            }
            const end = text.indexOf("'''", start + 3);
            if (end === -1) {
                throw new AdventureScriptValidationError('Unclosed triple-quoted script block in ## Scripts section');
            }
            blocks.push(text.slice(start + 3, end));
            i = end + 3;
        }
        return blocks;
    }
    /** @deprecated Use {@link validateAdventurePrelude} from adventureScriptValidation — kept for tests. */
    static validatePrelude(prelude) {
        (0, adventureScriptValidation_1.validatePrelude)(prelude);
    }
    static isScriptsSectionHeader(trimmedLine) {
        if (!trimmedLine.startsWith('## ')) {
            return false;
        }
        const name = trimmedLine.substring(3).trim().toLowerCase();
        return this.SCRIPTS_SECTION_NAMES.has(name);
    }
}
exports.AdventureScriptsParser = AdventureScriptsParser;
AdventureScriptsParser.SCRIPTS_SECTION_NAMES = new Set(['scripts']);
//# sourceMappingURL=AdventureScriptsParser.js.map